import React, { useState, useEffect, useRef } from 'react';
import AttributeEditor from '@amzn/awsui-components-console/attribute-editor';
import Input, { InputProps } from '@amzn/awsui-components-console/input';
import Button from '@amzn/awsui-components-console/button';
import TextContent from '@amzn/awsui-components-console/text-content';
import './LinkEditPopover.scss';

// Constants
const POPOVER_WIDTH = 500; // should equal to .popover-bubble.width
const POPOVER_HEIGHT = 300;
const ARROW_HEIGHT = 8;
const SPACING = 4;
const EDGE_MARGIN = 20;

interface LinkEditPopoverProps {
  anchorElement: HTMLAnchorElement | null;
  onSave: () => void;
  onClose: () => void;
}

interface SESTag {
  key?: string;
  value?: string;
}

// Helper functions
const calculatePlacement = (rect: DOMRect) => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const viewportHeight = window.innerHeight;
  const MARGIN_FROM_EDGE = 20; // Minimum distance from viewport edge

  // Calculate available space above and below
  const spaceAbove = rect.top - MARGIN_FROM_EDGE;
  const spaceBelow = viewportHeight - rect.bottom - MARGIN_FROM_EDGE;

  // Determine placement based on available space
  if (rect.top < POPOVER_HEIGHT + ARROW_HEIGHT + SPACING) {
    // Not enough space above, place below
    return {
      placement: 'bottom' as const,
      top: rect.bottom + scrollTop + ARROW_HEIGHT + SPACING,
      maxHeight: Math.min(400, spaceBelow - ARROW_HEIGHT - SPACING),
    };
  } else {
    // Place above (default)
    return {
      placement: 'top' as const,
      top: rect.top + scrollTop - ARROW_HEIGHT - SPACING,
      maxHeight: Math.min(400, spaceAbove - ARROW_HEIGHT - SPACING),
    };
  }
};

const calculateHorizontalPosition = (rect: DOMRect) => {
  const viewportWidth = window.innerWidth;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const idealLeft = rect.left + rect.width / 2;
  let actualLeft = idealLeft;
  let arrowOffset = 0;

  if (idealLeft - POPOVER_WIDTH / 2 < EDGE_MARGIN) {
    actualLeft = POPOVER_WIDTH / 2 + EDGE_MARGIN;
    arrowOffset = idealLeft - actualLeft;
  } else if (idealLeft + POPOVER_WIDTH / 2 > viewportWidth - EDGE_MARGIN) {
    actualLeft = viewportWidth - POPOVER_WIDTH / 2 - EDGE_MARGIN;
    arrowOffset = idealLeft - actualLeft;
  }

  const maxArrowOffset = POPOVER_WIDTH / 2 - 20;
  arrowOffset = Math.max(-maxArrowOffset, Math.min(maxArrowOffset, arrowOffset));

  return { left: actualLeft + scrollLeft, arrowOffset };
};

const LinkEditPopover: React.FC<LinkEditPopoverProps> = ({ anchorElement, onSave, onClose }) => {
  const [hrefValue, setHrefValue] = useState('');
  const [sesTags, setSesTags] = useState<SESTag[]>([]);
  const [hasNoTrack, setHasNoTrack] = useState(false);
  const [popoverState, setPopoverState] = useState({
    position: { top: 0, left: 0 },
    placement: 'top' as 'top' | 'bottom',
    arrowOffset: 0,
    maxHeight: 400,
  });
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize href and tags only when anchor element changes
  useEffect(() => {
    if (anchorElement) {
      // Get the href attribute value directly
      const currentHref = anchorElement.getAttribute('href') || '';
      setHrefValue(currentHref);

      // Check for ses:no-track attribute
      const noTrackAttr = anchorElement.hasAttribute('ses:no-track');
      setHasNoTrack(noTrackAttr);

      // Parse existing SES tags if present
      const sesTagsAttr = anchorElement.getAttribute('ses:tags');
      if (sesTagsAttr) {
        const parsedTags = sesTagsAttr
          .split(';')
          .map((pair) => {
            const [key, value] = pair.split(':');
            return { key: key || undefined, value: value || undefined };
          })
          .filter((tag) => tag.key || tag.value);
        setSesTags(parsedTags);
      } else {
        setSesTags([]);
      }
    }
  }, [anchorElement]);

  // Handle positioning separately
  useEffect(() => {
    if (anchorElement && popoverRef.current) {
      const rect = anchorElement.getBoundingClientRect();

      const { placement, top, maxHeight } = calculatePlacement(rect);
      const { left, arrowOffset } = calculateHorizontalPosition(rect);

      setPopoverState({
        position: { top, left },
        placement,
        arrowOffset,
        maxHeight,
      });
    }
  }, [anchorElement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Consolidated position update function
    const updatePosition = (recalculateHorizontal: boolean = false) => {
      if (!anchorElement) return;

      const rect = anchorElement.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        // Element is no longer visible
        onClose();
        return;
      }

      const { placement, top, maxHeight } = calculatePlacement(rect);

      if (recalculateHorizontal) {
        // For resize, recalculate horizontal position too
        const { left, arrowOffset } = calculateHorizontalPosition(rect);
        setPopoverState({
          position: { top, left },
          placement,
          arrowOffset,
          maxHeight,
        });
      } else {
        // For scroll, only update vertical position and placement
        setPopoverState((prev) => ({
          ...prev,
          position: { ...prev.position, top },
          placement,
          maxHeight,
        }));
      }
    };

    const handleScroll = () => updatePosition(false);
    const handleResize = () => updatePosition(true);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose, anchorElement]);

  const handleSave = () => {
    // Save URL with https:// prefix if needed
    let processedHref = hrefValue.trim();

    // Format SES tags
    const sesTagsString = sesTags
      .filter((tag) => tag.key && tag.value)
      .map((tag) => `${tag.key}:${tag.value}`)
      .join(';');

    // Pass both URL and SES tags to parent
    if (anchorElement) {
      anchorElement.setAttribute('href', processedHref);

      if (sesTagsString) {
        anchorElement.setAttribute('ses:tags', sesTagsString);
      } else {
        anchorElement.removeAttribute('ses:tags');
      }
    }

    onSave();
    onClose();
  };

  const handleTagChange = (index: number, field: 'key' | 'value', value: string) => {
    const newTags = [...sesTags];
    newTags[index][field] = value;
    setSesTags(newTags);
  };

  const handleKeyDown = (event: CustomEvent<InputProps.KeyDetail>) => {
    if (event.detail.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
  };

  if (!anchorElement) {
    return null;
  }

  const transformStyle = popoverState.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';

  return (
    <div
      ref={popoverRef}
      className={`link-edit-popover ${popoverState.placement}`}
      style={{
        position: 'absolute',
        top: `${popoverState.position.top}px`,
        left: `${popoverState.position.left}px`,
        transform: transformStyle,
        zIndex: 10000,
      }}
    >
      <div className="popover-bubble" style={{ maxHeight: `${popoverState.maxHeight}px`, width: POPOVER_WIDTH }}>
        <div className="popover-scrollable">
          <div className="popover-content">
            <div className="field-group">
              <TextContent>
                <h5>URL</h5>
              </TextContent>
              <Input
                ref={inputRef}
                type="text"
                value={hrefValue}
                onChange={({ detail }) => setHrefValue(detail.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter URL"
              />
            </div>

            <div className="field-group">
              <AttributeEditor
                onAddButtonClick={() => !hasNoTrack && setSesTags([...sesTags, {}])}
                onRemoveButtonClick={({ detail: { itemIndex } }) => {
                  const tmpItems = [...sesTags];
                  tmpItems.splice(itemIndex, 1);
                  setSesTags(tmpItems);
                }}
                items={sesTags}
                gridLayout={[
                  { rows: [[1, 2]] }, // Both fields in one row for all screen sizes
                ]}
                addButtonText={hasNoTrack ? 'Add disabled (ses:no-track)' : 'Add SES tag'}
                removeButtonText="Remove"
                disableAddButton={hasNoTrack}
                definition={[
                  {
                    label: 'SES-Key',
                    control: (item, itemIndex) => (
                      <Input
                        value={item.key || ''}
                        onChange={({ detail }) => handleTagChange(itemIndex, 'key', detail.value)}
                        placeholder="Enter key"
                      />
                    ),
                  },
                  {
                    label: 'SES-Value',
                    control: (item, itemIndex) => (
                      <Input
                        value={item.value || ''}
                        onChange={({ detail }) => handleTagChange(itemIndex, 'value', detail.value)}
                        placeholder="Enter value"
                      />
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="button-group">
          <Button variant="link" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Apply
          </Button>
        </div>
      </div>

      <div
        className="popover-arrow"
        style={{
          left: `calc(50% + ${popoverState.arrowOffset}px)`,
        }}
      />
    </div>
  );
};

export default LinkEditPopover;
